import React from 'react';
import { ToastContext, Form, Button, Icon, InputLabel, Input, SelectLabel, SelectHierarchyLabel, OptionalSection } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { fetcher } from '@weezevent/weezjs-core';
import { useNotifications, Notification } from '../../../../../../components/notifications/notificationsProvider';
import useAppContext from '../../../../../../context/useAppContext';
import moment from 'moment-timezone';
import LoadingCard from '../../../../../../components/loadingCard';
import css from './modal-popup.module.css';

function IsProcessingAsyncBulkUpdateSeats(seatUuids) {
    // Check if an async bulk seat update operation is already in progress
    // determine if any of the provided seat UUIDs are currently being processed.
    // This prevents duplicate update requests for the same seats.
    const strLocalStorage = window.localStorage.getItem('weez_notifications');
    let currentNotifications = strLocalStorage?.length > 0 ? JSON.parse(strLocalStorage) : [];
    return currentNotifications.some(notif => {
        return notif.sub_type === 'bulk_update_seats' && seatUuids.some(item => notif.context.includes(item));
    });
}

function SelectedSeatsModal({
    form,
    setForm,
    organization,
    zone,
    chartId,
    categories,
    contingents,
    inventories,
    inventoriesOptions,
    sync,
    synczones,
    setFormObject,
    inputError,
    inputValidity = () => {}
}) {
    const { t } = useTranslation();
    const [{ chart }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const [submitting, setSubmitting] = React.useState(false);
    const { addNotification } = useNotifications();

    const selectedSeatsCount = React.useMemo(() => {
        return form.selectedSeats.length;
    }, [form]);

    const categoriesOptions = React.useMemo(() => {
        let categoriesList = [
            { text: t('category.none'), value: 'none' },
            { text: t('category.keep_applied_category'), value: 'default' }
        ];
        categories.forEach(cat => {
            categoriesList.push({ text: cat.name, value: cat.uuid });
        });
        return categoriesList;
    }, [categories]);

    const contingentsOptions = React.useMemo(() => {
        let contingentsList = [
            { text: t('contingent.none'), value: 'none' },
            { text: t('contingent.keep_applied_contingent'), value: 'default' }
        ];
        contingents.forEach(cat => {
            contingentsList.push({ text: cat.name, value: cat.uuid });
        });
        return contingentsList;
    }, [contingents]);

    const propagationOptions = React.useMemo(() => {
        let options = [{ text: t('ticket.charts.zone.propagate.options.all'), value: 0 }];
        if (inventories && inventories.length) {
            options = [...options, { text: t('ticket.charts.zone.propagate.options.active'), value: 1 }, { text: t('ticket.charts.zone.propagate.options.manual'), value: 2 }];
        }
        return options;
    });

    const manualSelection = React.useMemo(() => {
        return form.propagationMode === 2;
    }, [form, form.propagationMode]);

    const inventoriesUuids = React.useMemo(() => {
        return inventories?.map(i => {
            return i.uuid;
        });
    }, [inventories]);

    const activeInventories = React.useMemo(() => {
        return inventories
            .filter(iv => iv.active && (iv.end_date === null || iv.end_date < moment().toISOString()))
            .map(i => {
                return i.uuid;
            });
    }, [inventories]);

    const payload = React.useMemo(() => {
        const list = [];
        form.selectedSeats.forEach(s => {
            let seat = {
                uuid: s.uuid,
                access: form.access || s.access,
                label: form.label || s.label,
                propagate_to_inventories: inventoriesUuids
            };

            if (form.selectedSeats.length === 1) {
                seat.row = form.row || s.row;
                seat.seat = form.seat || s.seat;
            }

            if (form.selectedContingent !== 'default') {
                seat['contingent'] = form.selectedContingent === 'none' ? null : form.selectedContingent;
            }
            if (form.selectedCategory !== 'default') {
                seat['category'] = form.selectedCategory === 'none' ? null : form.selectedCategory;
            }
            switch (form.propagationMode) {
                case 0:
                    break;
                case 1:
                    seat['propagate_to_inventories'] = activeInventories;
                    break;
                case 2:
                    seat['propagate_to_inventories'] = form.selectedInventories;
                    break;
                default:
                    break;
            }
            list.push(seat);
        });
        return list;
    }, [form, inventoriesUuids, activeInventories]);

    const disableSubmit = React.useMemo(() => {
        const inputs = [form.access, form.row, form.seat];

        return !inputs.every(inputValidity) || submitting;
    }, [form, submitting, inputValidity]);

    const handleSubmit = React.useCallback(() => {
        setSubmitting(true);
        if (chart.has_sessions) {
            // asynchrone bulk update
            // --> propagate to all sessions inventories by providing [] in propagate_to_inventories
            const seatUuids = payload.map(item => item.uuid);

            // prevent to send multiple async bulk update seats on the same seat while task is processing
            if (IsProcessingAsyncBulkUpdateSeats(seatUuids)) {
                setForm('selectedSeats', []);
                toast.error(t('ticket.manager.attendees.action.generic.toast.async_in_progress'));
                return;
            }

            const payloadFlowey = {
                type: 'async_bulk_update_seats',
                config: payload
            };

            fetcher
                .post(`/ticket/organizations/${organization.id}/charts/${chartId}/zones/${zone.uuid}/seats/actions`, { data: payloadFlowey })
                .then(response => {
                    setForm('selectedSeats', []);
                    addNotification(
                        new Notification('async_actions', 'bulk_update_seats', seatUuids, response?.response?.task_id, {
                            success: 'ticket.charts.zone.edition_success',
                            error: 'ticket.charts.zone.edition_failure'
                        })
                    );
                    toast.info(t('ticket.manager.attendees.action.generic.toast.async'));
                })
                .catch(e => {
                    toast.error(t('ticket.charts.newZone.failure'));
                })
                .finally(() => {
                    setSubmitting(false);
                });
        } else {
            // synchrone bulk update --> seated inventories
            fetcher
                .patch(`/ticket/organizations/${organization.id}/charts/${chartId}/zones/${zone.uuid}/seats`, { data: payload })
                .then(() => {
                    toast.success(t('ticket.charts.zone.edition_success'));
                    setForm('selectedSeats', []);
                    sync();
                    synczones();
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.charts.newZone.failure'));
                })
                .finally(() => {
                    setSubmitting(false);
                });
        }
    }, [chart, chartId, organization, zone, payload, sync, synczones]);

    return (
        <div className={css['modal-popup-container']}>
            <div className={css['modal-popup']}>
                <div className={css['modal-popup-header']}>
                    <div className={css['modal-popup-header-title']}>{`${selectedSeatsCount} ${t('seats_selected', { count: selectedSeatsCount })}`}</div>
                    <Icon
                        className={css['modal-popup-header-toggle']}
                        name="cross"
                        onClick={() =>
                            setFormObject({
                                ...form,
                                selectedSeats: [],
                                access: null,
                                row: null,
                                label: null,
                                seat: null,
                                selectedContingent: 'default',
                                selectedCategory: 'default'
                            })
                        }
                        size="regular"
                    />
                </div>

                <div className={css['modal-popup-body']}>
                    <LoadingCard loading={submitting}>
                        <SeatTitle form={form} categories={categories} />
                        <Form.Field>
                            <InputLabel label={t('ticket.charts.seats.access')} />
                            <Input
                                errors={Boolean(!inputValidity(form.access))}
                                onChange={({ target: { value } }) => setForm('access', value.trim().slice(0, 20))}
                                value={form.access || form.selectedSeats[0]?.access}
                            />
                            <Form.FieldErrors errors={[inputError(form.access)]} />
                        </Form.Field>
                        {selectedSeatsCount === 1 && (
                            <>
                                <Form.Field>
                                    <InputLabel label={t('ticket.charts.seats.row')} />
                                    <Input
                                        errors={Boolean(!inputValidity(form.row))}
                                        onChange={({ target: { value } }) => setForm('row', value.slice(0, 20))}
                                        value={form.row || form.selectedSeats[0]?.row}
                                    />
                                    <Form.FieldErrors errors={[inputError(form.row)]} />
                                </Form.Field>

                                <Form.Field>
                                    <InputLabel label={t('ticket.charts.seats.seat')} />
                                    <Input
                                        errors={Boolean(!inputValidity(form.seat))}
                                        onChange={({ target: { value } }) => setForm('seat', value.slice(0, 20))}
                                        value={form.seat || form.selectedSeats[0]?.seat}
                                    />
                                    <Form.FieldErrors errors={[inputError(form.seat)]} />
                                </Form.Field>
                            </>
                        )}

                        <div className={css['modal-popup-select']}>
                            <SelectLabel
                                label={t('ticket.common.seating.category')}
                                value={form.selectedCategory}
                                options={categoriesOptions}
                                onChange={value => {
                                    setForm('selectedCategory', value);
                                }}
                                useNew
                            />
                        </div>
                        <div className={css['modal-popup-select']}>
                            <SelectLabel
                                label={t('ticket.common.seating.contingent')}
                                value={form.selectedContingent}
                                options={contingentsOptions}
                                onChange={value => {
                                    setForm('selectedContingent', value);
                                }}
                                useNew
                            />
                        </div>

                        <OptionalSection label={t('ticket.charts.zone.propagate.label')}>
                            <SelectLabel
                                label={t('ticket.common.inventories')}
                                value={form.propagationMode}
                                options={propagationOptions}
                                onChange={value => setForm('propagationMode', value)}
                                disabled={!inventories.length}
                                useNew
                            />
                        </OptionalSection>
                        {manualSelection && (
                            <SelectHierarchyLabel
                                multi
                                height="auto"
                                searchable={false}
                                placeholder={t('ticket.common.action.select')}
                                options={inventoriesOptions.filter(i => i.value !== 'default')}
                                value={form.selectedInventories.length ? t('ticket.charts.chart.inventories_selected', { count: form.selectedInventories.length }) : null}
                                selected={form.selectedInventories}
                                label={t('ticket.common.inventories')}
                                onChange={selected => setForm('selectedInventories', selected)}
                            />
                        )}
                    </LoadingCard>
                </div>

                <div className={css['modal-popup-footer']}>
                    <Button fluid primary onClick={handleSubmit} label={t(`ticket.common.action.save`)} disabled={disableSubmit} />
                </div>
            </div>
        </div>
    );
}

export default SelectedSeatsModal;

export function SeatTitle({ form, categories }) {
    const seat = form.selectedSeats[0];
    const { t } = useTranslation();

    const category = React.useMemo(() => {
        let cat = categories.find(cat => seat.category === cat.uuid);
        if (!cat) {
            return '';
        }
        return cat.name;
    }, [seat, categories]);

    if (!form.selectedSeats.length || form.selectedSeats.length > 1) {
        return null;
    }
    return (
        <span className={css['selected-seats-title']}>
            <span>
                <Icon name="seat" />
            </span>
            <span>{`${t('ticket.common.seating.category')} ${category}`}</span>
            <li></li>
            <span>{`${t('ticket.attendees.import.errors.details.seat_seat')} ${seat.seat}`}</span>
            <li></li>
            <span>{`${t('ticket.attendees.import.errors.details.seat_row')} ${seat.row}`}</span>
            <li></li>
            <span>{`${t('ticket.attendees.import.errors.details.seat_access')} ${seat.access}`}</span>
        </span>
    );
}
